import React, { useState } from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Table, Button, Switch } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import './ruleSettingsPage.less';
import { isMobile } from 'utils/detectDevice';

interface DataType {
  key: string,
  id: number;
  name: string;
  autonomousMethod: string;
  lastUpdatedDate: string | any;
}

interface EditableTableProps {
  data: any;
  columns: any;
  onEdit?: any;
  onToggleChange?: any;
  loadingId?: any;
  showIsActive?: boolean;
  showAction?: boolean;
  scroll?: any;
  onRow?: any;
  loading?: boolean;
  hidePagination?: boolean;
  handlePageChange?: any;
  isDataSorted?: any
  isCheckable?: boolean;
  selectionType?: 'checkbox' | 'radio';
  selectedRowKeys?: React.Key[];
  onSelectionChange?: (selectedKeys: React.Key[], selectedRows: any[]) => void;
  rowKeyField?: string;
  pageSize?: number;
  showSizeChanger?: boolean;
  pageSizeOptions?: string[];
  showTotal?: boolean;
}

function sortDataSource(data: any[]) {
  return data?.length ? data?.sort((a: any, b: any) => {
    // Extract the type (_IR or _SP) and the number
    const [aType, aNum] = a?.name?.split(/[_-]/)?.slice(-2) || [];
    const [bType, bNum] = b?.name?.split(/[_-]/)?.slice(-2) || [];

    // Sort by the number first
    const numComparison = parseInt(aNum, 10) - parseInt(bNum, 10);
    if (numComparison !== 0) {
      return numComparison;
    }

    // If numbers are the same, prioritize IR over SP
    if (aType !== bType) {
      return aType === 'IR' ? -1 : 1;
    }

    // If type and number are the same, sort by date
    const aDate = new Date(a?.schedule?.starting_time_window).getTime();
    const bDate = new Date(b?.schedule?.starting_time_window).getTime();
    return aDate - bDate; // Ascending order for dates
  }) : [];
}

const EditableTable: React.FC<EditableTableProps> = (props: any) => {
  const {
    data, columns, onEdit, onToggleChange, loadingId, showIsActive,
    showAction, scroll, onRow, loading, hidePagination, rowClassName,
    handlePageChange, isDataSorted, isCheckable, selectedRowKeys = [],
    onSelectionChange, rowKeyField, pageSize, selectionType = 'checkbox',
    showSizeChanger, pageSizeOptions, showTotal,
  } = props;
  const [isEdit, setIsEdit] = useState(false);
  const [tablePagination, setTablePagination] = useState<any>({
    pageSize: pageSize || (isMobile ? 8 : 10),
  });
  const { t } = useTranslation();

  const dataSource = (!isDataSorted ? sortDataSource(data) : data)
    ?.map((row: any, index: any) => ({ key: row?.key ?? String(index), ...row }));

  // Append edit column as default
  const updatedColumns: ColumnsType<DataType> = [
    ...columns,
    (showAction ? {
      title: t('Action'),
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => handleEdit(record)}
        >
          {t('Edit')}
        </Button>
      ),
      width: 100
    } : undefined),
    (showIsActive ? {
      title: t('Is Active'),
      key: 'is_active',
      fixed: 'right',
      render: (_, record: {schedule: {id: number, is_active: boolean}}) => {
        const { id, is_active: isActive } = record?.schedule || {};
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            defaultChecked={isActive}
            onChange={(d) => handleToggleChange(record, d)}
            loading={loadingId === id}
          />
        );
      },
      width: 90
    } : undefined),
  ];

  const rowSelection = isCheckable
    ? {
      type: selectionType,
      selectedRowKeys,
      onChange: (keys: React.Key[], rows: any[]) => onSelectionChange?.(keys, rows),
    }
    : undefined;

  const handleEdit = (record: any) => {
    setIsEdit(true);
    if (onEdit) onEdit(record);
  };

  const handleToggleChange = (record: any, isActive: boolean) => {
    const updatedRecord = {
      ...record,
      schedule: { ...(record?.schedule || {}), is_active: isActive }
    };
    onToggleChange(updatedRecord);
  };

  return (
    <div className="rule-settings-table">
      {!isEdit && (
      <Table
        scroll={scroll || { x: (columns?.length * 200) }}
        dataSource={dataSource}
        columns={updatedColumns?.filter((col) => col)}
        rowSelection={rowSelection}
        pagination={!hidePagination ? {
          ...(!hidePagination ? tablePagination : {}),
          onChange: handlePageChange || undefined,
          showSizeChanger,
          pageSizeOptions,
          showTotal: showTotal ? (total) => `${total} ${t('records')}` : undefined,
        } : false}
        onRow={onRow}
        loading={loading}
        onChange={(pagination) => setTablePagination(pagination)}
        locale={{ emptyText: t('No Data') }}
        rowClassName={rowClassName}
        rowKey={(record: any) => (rowKeyField ? record?.[rowKeyField] : record?.key || record?.id)}
      />
      )}
    </div>
  );
};

EditableTable.defaultProps = {
  data: [],
  columns: [],
  showIsActive: true,
  showAction: true,
  scroll: null,
  onRow: undefined,
  hidePagination: false,
  rowClassName: undefined,
  handlePageChange: undefined
};

export default EditableTable;
